<template>
	<div class="_navbar-page-link" :class="{ '-chapter': hasChapterLabel }">
		<Hyperlink v-if="!hasChapterLabel" :to="link" class="page relative">
			<figure class="m-c -e0">
				<Media :media="thumbnail" class="absolute fit-cover m-t" />
			</figure>
			<div class="label m-c">
				<span class="m-t -p-large">{{ label }}</span>
			</div>
		</Hyperlink>
		<Hyperlink v-else :to="link" class="page relative">
			<figure class="m-c -e0">
				<Media :media="thumbnail" class="absolute fit-cover m-t" />
			</figure>
			<div class="text-col">
				<div class="label-holder">
					<div class="-p-small chapter-label m-c">
						<span class="m-t">{{ chapterLabel }}</span>
					</div>
					<div class="-p-medium label m-c">
						<span class="m-t">{{ label }}</span>
					</div>
				</div>
				<div v-if="years" class="-p-small years label m-c">
					<span class="m-t">{{ years }}</span>
				</div>
			</div>
		</Hyperlink>
		<ul
			v-if="visibleChildren.length > 0"
			class="children flex p-small xs-p-tiny"
			:class="{ 'has-hovered-child': hoveredChildIndex !== null }"
			:style="childrenStyle"
		>
			<li
				v-for="(child, index) in visibleChildren"
				:key="index"
				class="relative flex align-center"
				@mouseenter="onChildMouseEnter(index)"
				@mouseleave="onChildMouseLeave"
			>
				<div class="children-divider m-t" />
				<Hyperlink :to="child.link" class="child-page relative items-center-center">
					<div class="label relative text-align-center m-c">
						<span class="m-t -p-small">{{ child.label }}</span>
					</div>
				</Hyperlink>
			</li>
		</ul>
	</div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { HyperLink as Hyperlink, MediaAsset as Media } from '@/components/core'

const props = defineProps({
	label: { type: String, required: true },
	link: { type: [String, Object], required: true },
	thumbnail: { type: [Boolean, Array, Object, String], default: false },
	children: { type: Array, default: () => [] },
	childrenGridCount: { type: Number, default: 0 },
	chapterLabel: { type: String, default: '' },
	years: { type: String, default: '' },
})

const hoveredChildIndex = ref(null)
const hasChapterLabel = computed(() => !!props.chapterLabel)
const children = computed(() => (Array.isArray(props.children) ? props.children : []))
const visibleChildren = computed(() => (hasChapterLabel.value ? [] : children.value))

const childrenStyle = computed(() => {
	const count = Math.max(visibleChildren.value.length, props.childrenGridCount, 1)
	const columns = Array(count).fill('minmax(0, 1fr)')

	if (hoveredChildIndex.value !== null) {
		const hoveredTrack = 1 + count / 7

		columns[hoveredChildIndex.value] = `minmax(0, ${hoveredTrack}fr)`
	}

	return {
		gridTemplateColumns: columns.join(' '),
	}
})

function onChildMouseEnter(index) {
	hoveredChildIndex.value = index
}

function onChildMouseLeave() {
	hoveredChildIndex.value = null
}
</script>

<style lang="scss" scoped>
@use '../atoms.scss';

._navbar-page-link {
	$thumbnail-desktop-height: 70;
	--y-padding: #{rs(12, 12)};
	--thumbnail-height: #{rs($thumbnail-desktop-height, 60)};

	position: relative;

	display: flex;
	justify-content: space-between;

	color: $white;

	.-p-large {
		font-size: rs(24, 20);
	}

	.-p-large,
	.-p-tiny,
	.-p-small {
		color: $white;
	}

	& > .page {
		flex-grow: 1;

		display: flex;
		align-items: center;
		gap: rs(16, 16);

		padding: var(--y-padding) var(--x-padding);
		padding-right: 8px;

		transition: filter 0.2s ease;

		.label {
			padding-right: 0.2em;
		}

		&:before {
			content: '';
			position: absolute;
			inset: 0;
			display: block;

			background: linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, transparent 90%);
			opacity: 0.025;
			transform: translateX(-100%);

			transition: transform 0.2s ease('power2', 'out');
			will-change: transform;
		}

		& > figure {
			position: relative;
			flex: 0 0 rs(90, 77);
			width: rs(90, 77);
			height: var(--thumbnail-height);

			border-radius: rs(2, 2);

			overflow: hidden;
			box-sizing: content-box;

			.media {
				position: absolute;
				width: rs(90, 77);
				height: var(--thumbnail-height);

				object-fit: cover;
			}

			video {
				filter: grayscale(1);
			}
		}

		@include hover-supported {
			& > figure,
			& > figure .media {
				transition: width 0.3s ease('m3.standard'), flex-basis 0.3s ease('m3.standard');
				will-change: width, flex-basis;
			}

			& > .label {
				transition: transform 0.2s ease('power3', 'inOut');
				transition-property: transform, opacity;
				will-change: transform, opacity;
			}

			&:hover {
				&:before {
					transform: translateX(0);
				}

				& > figure,
				& > figure .media {
					flex-basis: rs(102, 90);
					width: rs(102, 90);
				}

				& > .label {
					transform: translateX(8px);
				}
			}
		}

		&.-active.-exact {
			filter: brightness(0.5);
			pointer-events: none;

			&:before {
				transform: translateX(0);
				opacity: 0.05;
			}
		}
	}

	&.-chapter {
		& > .page {
			width: 100%;
			padding-right: var(--x-padding);

			.text-col {
				display: flex;
				flex-direction: row;
				justify-content: space-between;
				width: 100%;
				min-width: 0;
			}

			.label-holder {
				min-width: 0;
			}

			.chapter-label {
				padding-bottom: 8px;
				opacity: 0.4;
			}

			.label {
				padding-right: 0.2em;
			}

			.-p-medium {
				flex: 0 1 auto;

				@include max-width($sm) {
					padding-right: 12px;
				}
			}

			.years {
				align-self: flex-end;
				flex: 0 0 auto;
			}
		}
	}

	.children-divider {
		width: 1px;
		height: rs($thumbnail-desktop-height, 36);

		background-color: rgba($white, 0.06);
	}

	.children {
		width: 35%;
		flex: 0 0 35%;
		display: grid;
		margin-left: auto;
		transition: width 0.3s ease('power3'), flex-basis 0.3s ease('power3'),
			grid-template-columns 0.3s ease('power3');
		will-change: width, flex-basis, grid-template-columns;

		@include max-width($xs) {
			width: 45%;
			flex: 0 0 45%;
		}

		&.has-hovered-child {
			width: 40%;
			flex-basis: 40%;
		}
	}

	.children > li {
		justify-content: center;
		min-width: 0;
	}

	.child-page {
		flex: 1 1 0;
		width: calc(100% - #{rs(32, 28)});
		min-width: 0;
		height: var(--thumbnail-height);
		padding: var(--y-padding) rs(8, 8);
		margin: 0 auto;

		display: flex;
		justify-content: center;
		align-items: center;
		overflow: hidden;
		box-sizing: content-box;
		transition: filter 0.2s ease;

		.label {
			width: max-content;
			max-width: 100%;
			padding: 0 rs(8, 4);
			box-sizing: border-box;

			span {
				display: block;
				width: 100%;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}

		@include hover-supported {
			transition: padding 0.3s ease('power3'), filter 0.2s ease;
			will-change: padding;

			& > .label {
				transition: padding 0.3s ease('power3'), opacity 0.3s ease('power3');
				will-change: padding, opacity;
			}

			&:hover {
				padding-left: rs(16, 12);
				padding-right: rs(16, 12);

				& > .label {
					padding: 0 rs(16, 12);
					opacity: 0.66;
				}
			}
		}

		&.-active.-exact {
			filter: brightness(0.5);
			pointer-events: none;
		}
	}
}
</style>
